import { useState, useEffect } from "react";
import { API } from "@/App";
import DaxLiveHeader from "@/components/DaxLiveHeader";
import {
  TrendUp, TrendDown, Trophy, Lightning, Target, ChartBar,
  ArrowUp, ArrowDown, Calendar, Clock, Sparkle,
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
} from "recharts";

const COLORS = { profit: "#1D9E75", loss: "#D85A30", neutral: "#378ADD", flat: "#cbd5e1" };
const BOT_COLOR = { scalp: "#FF7F6E", day: "#378ADD", swing: "#14B8A6" };

const fmtEur = (v) => {
  const n = Number(v) || 0;
  const sign = n < 0 ? "-" : (n > 0 ? "+" : "");
  return `${sign}${Math.abs(n).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
};

// ===== Heatmap cell color (red→white→green based on PnL) =====
const heatColor = (pnl, max) => {
  if (!pnl || !max) return "#f1f5f9";
  const ratio = Math.min(Math.abs(pnl) / max, 1);
  if (pnl > 0) {
    const a = Math.round(ratio * 200);
    return `rgba(29, 158, 117, ${(a / 255).toFixed(2)})`;
  }
  const a = Math.round(ratio * 200);
  return `rgba(216, 90, 48, ${(a / 255).toFixed(2)})`;
};

// ===== Hour × Day heatmap =====
const HourDayHeatmap = ({ data }) => {
  if (!data || !data.cells) return null;
  const max = Math.max(0.01, ...data.cells.map(c => Math.abs(c.avg_pnl_eur)));
  const cellsByKey = {};
  data.cells.forEach(c => { cellsByKey[`${c.day_idx}-${c.hour}`] = c; });

  return (
    <div className="overflow-x-auto">
      <div className="inline-block min-w-full">
        <div className="grid" style={{ gridTemplateColumns: `60px repeat(${data.hours.length}, minmax(38px, 1fr))` }}>
          <div></div>
          {data.hours.map(h => (
            <div key={h} className="text-[10px] text-slate-400 font-data text-center pb-1 font-bold">
              {h}h
            </div>
          ))}
          {data.days.map((day, di) => (
            <>
              <div key={`label-${di}`} className="text-xs font-body text-slate-500 pr-2 self-center font-medium">
                {day.slice(0, 3)}
              </div>
              {data.hours.map(h => {
                const cell = cellsByKey[`${di}-${h}`];
                const c = cell ? cell.avg_pnl_eur : 0;
                const count = cell ? cell.count : 0;
                return (
                  <div
                    key={`${di}-${h}`}
                    className="aspect-square rounded-sm border border-slate-100 flex items-center justify-center text-[10px] font-data tabular-nums"
                    style={{
                      background: heatColor(c, max),
                      color: Math.abs(c) > max * 0.5 ? "white" : "#475569",
                    }}
                    title={cell && count ? `${day} ${h}h: ${count} trades, ${fmtEur(c)} avg` : "Pas de trade"}
                    data-testid={`heatmap-${di}-${h}`}
                  >
                    {count > 0 ? count : ""}
                  </div>
                );
              })}
            </>
          ))}
        </div>
        <p className="text-[10px] text-slate-400 mt-2 font-body">
          Nombre dans la cellule = nombre de trades. Couleur = P&L moyen (vert profit / rouge perte).
        </p>
      </div>
    </div>
  );
};

// ===== Calendar heatmap (last N days) =====
const CalendarHeatmap = ({ data }) => {
  if (!data || !data.days) return null;
  const max = Math.max(0.01, ...data.days.map(d => Math.abs(d.pnl_eur)));

  // Group into weeks for grid
  const weeks = [];
  let curWeek = Array(7).fill(null);
  data.days.forEach((d) => {
    const dayOfWeek = new Date(d.date).getDay(); // 0=Sun..6=Sat
    const idx = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Mon=0..Sun=6
    if (curWeek[idx] !== null) {
      weeks.push(curWeek);
      curWeek = Array(7).fill(null);
    }
    curWeek[idx] = d;
  });
  weeks.push(curWeek);

  const dayNames = ["L", "M", "M", "J", "V", "S", "D"];

  return (
    <div className="flex gap-1">
      <div className="flex flex-col justify-around pr-1">
        {dayNames.map((n, i) => (
          <span key={i} className="text-[9px] text-slate-400 font-bold w-3 h-3 flex items-center">{n}</span>
        ))}
      </div>
      <div className="flex gap-1 overflow-x-auto pb-1">
        {weeks.map((week, wi) => (
          <div key={wi} className="flex flex-col gap-1">
            {week.map((d, di) => (
              <div
                key={di}
                className="w-3 h-3 rounded-[2px] border border-slate-100"
                style={{ background: d ? heatColor(d.pnl_eur, max) : "transparent" }}
                title={d ? `${d.date}: ${d.trades} trades, ${fmtEur(d.pnl_eur)}` : ""}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

// ===== Bot performance card =====
const BotPerfCard = ({ bot, info }) => {
  const accent = BOT_COLOR[bot];
  return (
    <Card className="rounded-sm border-slate-200 shadow-none" data-testid={`adv-bot-${bot}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <span className="font-heading text-sm font-bold uppercase tracking-wider" style={{ color: accent }}>
            {info.label}
          </span>
          <span className="text-xs font-body text-slate-400">{info.trades} trades</span>
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-2">
        <div className="font-data text-2xl font-bold tabular-nums"
             style={{ color: info.total_pnl_eur >= 0 ? COLORS.profit : COLORS.loss }}>
          {fmtEur(info.total_pnl_eur)}
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
          <Stat label="Win rate" value={`${info.win_rate}%`} />
          <Stat label="Profit Factor" value={info.profit_factor.toFixed(2)} />
          <Stat label="Avg R/R" value={info.avg_risk_reward.toFixed(2)} />
          <Stat label="Wins / Losses" value={`${info.wins}W / ${info.losses}L`} />
        </div>
        {info.best_month && (
          <div className="text-[10px] text-slate-400 font-body pt-2 border-t border-slate-100 space-y-0.5">
            <div>Meilleur mois <span className="font-data text-emerald-700">{info.best_month.month}</span> : {fmtEur(info.best_month.pnl_eur)}</div>
            {info.worst_month && info.worst_month.month !== info.best_month.month && (
              <div>Pire mois <span className="font-data text-red-700">{info.worst_month.month}</span> : {fmtEur(info.worst_month.pnl_eur)}</div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const Stat = ({ label, value }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wider text-slate-400">{label}</p>
    <p className="font-data tabular-nums font-medium text-slate-800">{value}</p>
  </div>
);

// ===== Risk metric tile =====
const MetricTile = ({ icon: Icon, label, value, sub, tone = "neutral" }) => {
  const c = tone === "profit" ? COLORS.profit : tone === "loss" ? COLORS.loss : COLORS.neutral;
  return (
    <div className="bg-slate-50 rounded-sm p-3 border border-slate-100">
      <div className="flex items-center gap-2 mb-1">
        <Icon size={12} weight="bold" style={{ color: c }} />
        <span className="text-[10px] uppercase tracking-wider text-slate-400 font-body font-bold">{label}</span>
      </div>
      <p className="font-data text-lg font-bold tabular-nums" style={{ color: c }}>{value}</p>
      {sub && <p className="text-[10px] text-slate-400 font-body mt-0.5">{sub}</p>}
    </div>
  );
};

// ===== Main page =====
export default function AdvancedStats() {
  const [advanced, setAdvanced] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [calendar, setCalendar] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [a, h, c] = await Promise.all([
          fetch(`${API}/journal/advanced-stats`).then(r => r.json()),
          fetch(`${API}/journal/heatmap-hours`).then(r => r.json()),
          fetch(`${API}/journal/calendar-heatmap?days=90`).then(r => r.json()),
        ]);
        setAdvanced(a);
        setHeatmap(h);
        setCalendar(c);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading || !advanced) {
    return (
      <div className="container mx-auto px-4 py-6 max-w-[1400px]">
        <p className="text-slate-400 font-body">Chargement des statistiques avancées…</p>
      </div>
    );
  }

  const r = advanced.risk_metrics;
  const s = advanced.streaks;
  const f = advanced.frequency;
  const dirData = [
    { name: "BUY", value: advanced.direction.BUY.count, color: COLORS.profit },
    { name: "SELL", value: advanced.direction.SELL.count, color: COLORS.loss },
  ];

  return (
    <div className="container mx-auto px-4 lg:px-8 py-6 max-w-[1400px]" data-testid="advanced-stats-page">
      <DaxLiveHeader />

      <div className="mb-6">
        <h1 className="font-heading text-3xl font-bold text-slate-900">Statistiques Avancées</h1>
        <p className="text-sm text-slate-500 font-body mt-1">
          Analyse approfondie des performances par bot, heure, direction et risque — sur {advanced.total_closed} trades clôturés
        </p>
      </div>

      {/* Per-bot performance */}
      <div className="mb-6">
        <h2 className="font-heading text-xs uppercase tracking-[0.15em] text-slate-500 font-bold mb-3 flex items-center gap-2">
          <ChartBar size={14} weight="bold" /> Performance par bot
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {["scalp", "day", "swing"].map(bot => (
            <BotPerfCard key={bot} bot={bot} info={advanced.by_bot[bot]} />
          ))}
        </div>
      </div>

      {/* Hour × Day heatmap */}
      <Card className="rounded-sm border-slate-200 shadow-none mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
            <Clock size={14} weight="bold" /> Performance par heure × jour
          </CardTitle>
        </CardHeader>
        <CardContent>
          <HourDayHeatmap data={heatmap} />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        {/* Direction split */}
        <Card className="rounded-sm border-slate-200 shadow-none">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-sm font-bold uppercase tracking-wider text-slate-500">
              Direction (BUY vs SELL)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="w-24 h-24 shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={dirData} dataKey="value" innerRadius={25} outerRadius={45} paddingAngle={2}>
                      {dirData.map((d, i) => <Cell key={i} fill={d.color} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 space-y-2">
                <DirRow label="BUY (Long)" count={advanced.direction.BUY.count}
                        wr={advanced.direction.BUY.win_rate} pnl={advanced.direction.BUY.total_pnl_eur} color={COLORS.profit} />
                <DirRow label="SELL (Short)" count={advanced.direction.SELL.count}
                        wr={advanced.direction.SELL.win_rate} pnl={advanced.direction.SELL.total_pnl_eur} color={COLORS.loss} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Streaks */}
        <Card className="rounded-sm border-slate-200 shadow-none">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
              <Lightning size={14} weight="bold" /> Streaks & Régularité
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <MetricTile icon={ArrowUp} label="Max wins" value={`${s.max_win_streak}`} sub="série record" tone="profit" />
              <MetricTile icon={ArrowDown} label="Max losses" value={`${s.max_loss_streak}`} sub="série record" tone="loss" />
            </div>
            <div className="bg-slate-50 rounded-sm p-3 border border-slate-100">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Streak actuel</p>
              <p className="font-data text-lg font-bold"
                 style={{ color: s.current_kind === "win" ? COLORS.profit : s.current_kind === "loss" ? COLORS.loss : "#475569" }}>
                {s.current_kind === "win" ? `+${s.current_count} gains` :
                 s.current_kind === "loss" ? `${s.current_count} pertes` : "—"}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <Stat label="Trades / jour" value={f.per_day} />
              <Stat label="Trades / sem" value={f.per_week} />
            </div>
          </CardContent>
        </Card>

        {/* Risk metrics */}
        <Card className="rounded-sm border-slate-200 shadow-none">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
              <Target size={14} weight="bold" /> Métriques de risque
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <MetricTile icon={Sparkle} label="Sharpe" value={r.sharpe_ratio.toFixed(2)} sub={r.sharpe_ratio >= 1 ? "Solide" : "À améliorer"} tone={r.sharpe_ratio >= 1 ? "profit" : "neutral"} />
              <MetricTile icon={TrendDown} label="Max DD" value={fmtEur(-r.max_drawdown_eur)} tone="loss" />
              <MetricTile icon={Trophy} label="Recovery" value={r.recovery_factor.toFixed(2)} sub="net P&L / DD" tone={r.recovery_factor >= 3 ? "profit" : "neutral"} />
              <MetricTile icon={TrendUp} label="W/L Ratio" value={r.winner_loser_ratio.toFixed(2)} sub={`avg W ${fmtEur(r.avg_winner)} / L ${fmtEur(-r.avg_loser)}`} tone={r.winner_loser_ratio >= 1 ? "profit" : "loss"} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Calendar heatmap */}
      <Card className="rounded-sm border-slate-200 shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
            <Calendar size={14} weight="bold" /> Calendrier mensuel — 90 derniers jours
          </CardTitle>
        </CardHeader>
        <CardContent>
          <CalendarHeatmap data={calendar} />
          <div className="flex items-center gap-3 mt-3 text-[10px] font-body text-slate-400">
            <span>Moins</span>
            <div className="flex gap-1">
              {[-1, -0.5, 0, 0.5, 1].map(v => (
                <div key={v} className="w-3 h-3 rounded-[2px]" style={{ background: heatColor(v, 1) || "#f1f5f9" }} />
              ))}
            </div>
            <span>Plus</span>
            <span className="ml-3">🟥 Pertes • ⬜ Aucun trade • 🟩 Profits</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

const DirRow = ({ label, count, wr, pnl, color }) => (
  <div className="flex items-center justify-between text-xs">
    <div>
      <p className="font-bold" style={{ color }}>{label}</p>
      <p className="text-[10px] text-slate-400">{count} trades • {wr}% WR</p>
    </div>
    <p className="font-data tabular-nums font-bold" style={{ color: pnl >= 0 ? COLORS.profit : COLORS.loss }}>
      {fmtEur(pnl)}
    </p>
  </div>
);
